import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, redirect, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/accounting/contacts` — the address book.
 *
 * The list is a list: a row leads to the contact's own page rather than
 * unfolding a panel underneath, so a contact has an address that can be
 * linked to from a document, a customer, or a message.
 *
 * Everybody we invoice or are invoiced by, in one list. A partner that
 * is both sides at once is one row, not two — which is the whole reason
 * this page exists next to Customers: a customer is a project
 * relationship, a contact is a billing identity, and the landlord is
 * only ever the second.
 */

const searchSchema = z.object({
  role: z.enum(["all", "customer", "supplier", "both"]).optional(),
  q: z.string().optional(),
});

type Contact = {
  id: string;
  name: string;
  role: "customer" | "supplier" | "both";
  registrationNo: string | null;
  vatNo: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  defaultDueDays: number | null;
  currency: string;
  note: string | null;
  active: boolean;
  customerCount: number;
  issuedCount: number;
  receivedCount: number;
};

export const Route = createFileRoute("/_app/accounting/contacts/")({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({ role: search.role, q: search.q }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.role && deps.role !== "all") params.set("role", deps.role);
    if (deps.q) params.set("q", deps.q);
    const qs = params.toString();

    try {
      return await api.get<{ contacts: Contact[] }>(
        `/api/accounting/contacts${qs ? `?${qs}` : ""}`,
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: ContactsPage,
});

function ContactsPage() {
  const { t } = useTranslation();
  const { contacts } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const [adding, setAdding] = useState(false);

  const role = search.role ?? "all";

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">{t("accContacts.title")}</h1>
          <p className="text-muted-foreground text-sm">{t("accContacts.subtitle")}</p>
        </div>
        <Button onClick={() => setAdding((value) => !value)}>
          {adding ? t("accContacts.close") : t("accContacts.newContact")}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {(["all", "customer", "supplier", "both"] as const).map((option) => (
          <button
            key={option}
            type="button"
            className={`rounded border px-3 py-1 text-sm ${role === option ? "bg-muted" : ""}`}
            onClick={() =>
              navigate({ to: "/accounting/contacts", search: { ...search, role: option } })
            }
          >
            {option === "all" ? t("accContacts.all") : t(`accContacts.role.${option}`)}
          </button>
        ))}
        <input
          defaultValue={search.q ?? ""}
          placeholder={t("accContacts.searchPlaceholder")}
          className="h-9 min-w-64 flex-1 rounded-md border border-input bg-background px-3 text-sm"
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            navigate({
              to: "/accounting/contacts",
              search: { ...search, q: e.currentTarget.value || undefined },
            });
          }}
        />
      </div>

      {adding && (
        <ContactForm
          onDone={() => {
            setAdding(false);
            void router.invalidate();
          }}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          {contacts.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("accContacts.empty")}</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">{t("accContacts.col.name")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accContacts.col.role")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accContacts.col.registration")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accContacts.col.vatNo")}</th>
                  <th className="py-1 pr-4 font-medium">{t("accContacts.col.linkedCustomers")}</th>
                  <th className="py-1 pr-4 text-right font-medium">
                    {t("accContacts.col.documents")}
                  </th>
                  <th className="py-1 font-medium" />
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id} className="border-t">
                    <td className="py-1 pr-4">
                      <Link
                        to="/accounting/contacts/$id"
                        params={{ id: contact.id }}
                        className="font-medium hover:underline"
                      >
                        {contact.name}
                      </Link>
                    </td>
                    <td className="py-1 pr-4">
                      <Badge variant={contact.role === "both" ? "success" : "outline"}>
                        {t(`accContacts.role.${contact.role}`)}
                      </Badge>
                    </td>
                    <td className="py-1 pr-4 tabular-nums">{contact.registrationNo ?? "—"}</td>
                    <td className="py-1 pr-4">{contact.vatNo ?? "—"}</td>
                    <td className="py-1 pr-4 text-muted-foreground">
                      {contact.customerCount > 0 ? contact.customerCount : "—"}
                    </td>
                    <td className="py-1 pr-4 text-right tabular-nums">
                      {contact.issuedCount + contact.receivedCount}
                    </td>
                    <td className="py-1">
                      <Link
                        to="/accounting/contacts/$id"
                        params={{ id: contact.id }}
                        className="text-sm underline"
                      >
                        {t("accContacts.profile")}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type AresSubject = {
  registrationNo: string;
  name: string;
  vatNo: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
};

function ContactForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const create = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/contacts", body),
    { onSuccess: onDone, errorMessage: () => t("accContacts.createError") },
  );
  const formRef = useRef<HTMLFormElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<AresSubject[]>([]);
  const [lookup, setLookup] = useState<"idle" | "loading" | "notFound">("idle");
  useEffect(() => () => clearTimeout(timer.current), []);

  const fill = (subject: AresSubject) => {
    const form = formRef.current;
    if (!form) return;
    const values: Record<string, string | null> = {
      name: subject.name,
      registrationNo: subject.registrationNo,
      vatNo: subject.vatNo,
      street: subject.street,
      city: subject.city,
      zip: subject.zip,
      country: subject.country,
    };
    for (const [key, value] of Object.entries(values)) {
      const input = form.elements.namedItem(key);
      if (input instanceof HTMLInputElement) input.value = value ?? "";
    }
    setSuggestions([]);
    setLookup("idle");
  };

  // A registration number fills the form outright; a name offers the matches to pick from.
  const ask = (query: string, byRegistrationNo: boolean) => {
    clearTimeout(timer.current);
    setSuggestions([]);
    setLookup("idle");
    if (query.trim().length < 3) return;
    timer.current = setTimeout(async () => {
      if (byRegistrationNo) setLookup("loading");
      try {
        const { subjects } = await api.get<{ subjects: AresSubject[] }>(
          `/api/accounting/ares?q=${encodeURIComponent(query)}`,
        );
        if (!byRegistrationNo) return setSuggestions(subjects);
        const [subject] = subjects;
        if (subject) fill(subject);
        else setLookup("notFound");
      } catch {
        setLookup("idle");
      }
    }, 300);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("accContacts.newTitle")}</CardTitle>
        <CardDescription>{t("accContacts.newDescription")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form
          ref={formRef}
          className="grid gap-3 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            const form = new FormData(e.currentTarget);
            const value = (key: string) => {
              const raw = String(form.get(key) ?? "").trim();
              return raw.length > 0 ? raw : null;
            };
            create.mutate({
              name: String(form.get("name") ?? "").trim(),
              role: String(form.get("role") ?? "customer"),
              registrationNo: value("registrationNo"),
              vatNo: value("vatNo"),
              email: value("email"),
              street: value("street"),
              city: value("city"),
              zip: value("zip"),
              country: String(form.get("country") ?? "CZ"),
              defaultDueDays: dueDays(form),
            });
          }}
        >
          <p className="text-muted-foreground text-xs md:col-span-3">
            {lookup === "loading"
              ? t("accContacts.ares.loading")
              : lookup === "notFound"
                ? t("accContacts.ares.notFound")
                : t("accContacts.ares.hint")}
          </p>
          <div className="relative">
            <Field
              name="name"
              label={t("accContacts.field.name")}
              required
              onChange={(value) => ask(value, false)}
              onBlur={() => setTimeout(() => setSuggestions([]), 200)}
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-popover text-sm shadow-md">
                {suggestions.map((subject) => (
                  <li key={subject.registrationNo}>
                    <button
                      type="button"
                      className="w-full px-3 py-2 text-left hover:bg-muted"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => fill(subject)}
                    >
                      <span className="block font-medium">{subject.name}</span>
                      <span className="block text-muted-foreground text-xs">
                        {subject.registrationNo}
                        {subject.city ? ` · ${subject.city}` : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <label className="text-sm">
            <span className="text-muted-foreground text-xs">{t("accContacts.field.role")}</span>
            <select
              name="role"
              className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="customer">{t("accContacts.role.customer")}</option>
              <option value="supplier">{t("accContacts.role.supplier")}</option>
              <option value="both">{t("accContacts.role.both")}</option>
            </select>
          </label>
          <Field
            name="registrationNo"
            label={t("accContacts.field.registrationNo")}
            onChange={(value) => {
              const digits = value.replace(/\s/g, "");
              if (/^\d{8}$/.test(digits)) ask(digits, true);
            }}
          />
          <Field name="vatNo" label={t("accContacts.field.vatNo")} />
          <Field name="email" label={t("accContacts.field.email")} type="email" />
          <Field name="street" label={t("accContacts.field.street")} />
          <Field name="zip" label={t("accContacts.field.zip")} />
          <Field name="city" label={t("accContacts.field.city")} />
          <Field name="country" label={t("accContacts.field.country")} defaultValue="CZ" />
          <Field
            name="defaultDueDays"
            label={t("accContacts.field.dueDays")}
            type="number"
            placeholder={t("accContacts.field.dueDaysPlaceholder")}
          />
          <div className="flex items-end">
            <Button type="submit" disabled={create.pending}>
              {create.pending ? t("accContacts.creating") : t("accContacts.create")}
            </Button>
          </div>
          {create.error && <p className="text-destructive text-sm md:col-span-3">{create.error}</p>}
        </form>
      </CardContent>
    </Card>
  );
}

const dueDays = (form: FormData): number | null => {
  const raw = String(form.get("defaultDueDays") ?? "").trim();
  return raw.length > 0 ? Number(raw) : null;
};

function Field(props: {
  name: string;
  label: string;
  type?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}) {
  const { label, onChange, ...rest } = props;
  return (
    <label className="block text-sm">
      <span className="text-muted-foreground text-xs">{label}</span>
      <input
        {...rest}
        autoComplete={onChange ? "off" : undefined}
        onChange={onChange ? (e) => onChange(e.currentTarget.value) : undefined}
        className="mt-1 block h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
      />
    </label>
  );
}
